import EditIcon from "@mui/icons-material/Edit";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { mapHighestLevelToCategory } from "../../../Utils/helperFunctions";
import {
  resolveCategoryWithFallback,
} from "../../../Utils/auctionRules";
import { PlayerRecord, SortConfig, SortKey } from "../types";

const commonCellStyles = {
  color: "rgba(255,255,255,0.92)",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "normal",
  borderBottom: "1px solid rgba(255,255,255,0.14)",
};

type PlayersTableProps = {
  activeView: "men" | "women";
  filteredCount: number;
  page: number;
  rowsPerPage: number;
  sortConfig: SortConfig;
  sortedPlayers: PlayerRecord[];
  onEditClick: (player: PlayerRecord) => void;
  onSendLiveTeamId: (teamId?: string) => Promise<void>;
  onSortChange: (key: SortKey) => void;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (value: number) => void;
};

const PlayersTable: React.FC<PlayersTableProps> = ({
  activeView,
  filteredCount,
  page,
  rowsPerPage,
  sortConfig,
  sortedPlayers,
  onEditClick,
  onSendLiveTeamId,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const columns: { label: string; sortKey?: SortKey }[] = [
    { label: "Auction No" },
    { label: "Name of the player", sortKey: "name" },
    { label: "Gender" },
    { label: "Age" },
    { label: "MCA ID", sortKey: "MCANo" },
    { label: "Role" },
    { label: "Base price" },
    { label: "Category" },
    { label: "set" },
    { label: "Sold for" },
    { label: "Status" },
    { label: "Sold to" },
    { label: "Bid price" },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: "12px",
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(22,23,30,0.84) 0%, rgba(16,17,20,0.9) 100%)",
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          background: "transparent",
          border: "none",
          backdropFilter: "blur(4px)",
          position: "relative",
          zIndex: 1,
          borderRadius: 0,
          overflow: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Table stickyHeader>
          <TableHead
            sx={{
              background:
                "linear-gradient(90deg, rgba(72,23,54,0.85) 0%, rgba(103,37,28,0.84) 100%)",
              "& .MuiTableCell-head": {
                background:
                  "linear-gradient(90deg, rgba(72,23,54,0.98) 0%, rgba(103,37,28,0.98) 100%)",
                color: "#fff",
                zIndex: 2,
              },
            }}
          >
            <TableRow sx={{ borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
              {columns.map(({ label, sortKey }, idx) => {
                const isActive = sortKey && sortConfig?.key === sortKey;
                const isSortable = Boolean(sortKey);
                const direction = isActive ? sortConfig?.direction : "asc";
                return (
                  <TableCell
                    key={idx}
                    sx={{
                      color: "#fff",
                      border: "none",
                      fontWeight: "600",
                      fontSize: "16px",
                      cursor: isSortable ? "pointer" : "default",
                      userSelect: "none",
                    }}
                    onClick={() => {
                      if (!sortKey) return;
                      onSortChange(sortKey);
                    }}
                  >
                    <Box display="flex" alignItems="center" gap="4px">
                      {label}
                      {isSortable && (
                        <span
                          style={{
                            fontSize: "14px",
                            opacity: isActive ? 1 : 0.5,
                          }}
                        >
                          {direction === "asc" ? (
                            <SwapVertIcon />
                          ) : (
                            <SwapVertIcon />
                          )}
                        </span>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPlayers
              ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              ?.map((player: PlayerRecord, index: number) => {
                const normalizedCategory = resolveCategoryWithFallback({
                  division: activeView === "women" ? "women" : "men",
                  basePrice: player?.basePrice,
                  mcaNo: player?.MCANo,
                  fallbackCategory: mapHighestLevelToCategory(
                    player?.recentCompetetionLevel || "",
                  ),
                  playerName: player?.name,
                  playerAge: player.playerAge,
                  birthData: player.birthData,
                });
                const soldStatus =
                  player.auctionResult === "Sold"
                    ? {
                        label: "Sold",
                        background: "#7DC789",
                        color: "#212120",
                      }
                    : player.auctionResult === "Unsold"
                      ? {
                          label: "Unsold",
                          background: "#F8A6A6",
                          color: "#212120",
                        }
                      : null;

                return (
                  <TableRow
                    key={player.playerId || player.playerRegId || index}
                    sx={{
                      borderBottom: "1px solid rgba(255,255,255,0.14)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.03)",
                      },
                    }}
                  >
                    <TableCell sx={commonCellStyles}>
                      {player?.auctionNumber ?? "--"}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>{player.name}</TableCell>
                    <TableCell sx={commonCellStyles}>
                      {player.gender || "--"}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      {player.playerAge ?? "--"}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>{player.MCANo}</TableCell>
                    <TableCell sx={commonCellStyles}>
                      {player.roleType || player.role || "--"}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      {player.basePrice != null
                        ? `₹${player.basePrice.toLocaleString("en-IN")}`
                        : "--"}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      {normalizedCategory}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>{player?.set}</TableCell>
                    <TableCell sx={commonCellStyles}>
                      {player.winningBid != null
                        ? `₹${player.winningBid.toLocaleString("en-IN")}`
                        : "--"}
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      {soldStatus ? (
                        <Chip
                          label={soldStatus.label}
                          sx={{
                            color: soldStatus.color,
                            background:
                              soldStatus.label === "Sold"
                                ? "linear-gradient(90deg, #77C788 0%, #8ED69C 100%)"
                                : soldStatus.background,
                            borderRadius: "8px",
                            height: "36px",
                            minWidth: "83px",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        />
                      ) : (
                        "--"
                      )}
                    </TableCell>
                    <TableCell
                      sx={commonCellStyles}
                      onClick={async () => {
                        await onSendLiveTeamId(player?.soldToId);
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Box
                          component={"img"}
                          sx={{ height: "25px" }}
                          src={player?.soldToLogo}
                          alt=""
                        />
                        {player?.soldTo || "--"}
                      </Box>
                    </TableCell>
                    <TableCell sx={commonCellStyles}>
                      <IconButton
                        color="primary"
                        onClick={() => onEditClick(player)}
                        sx={{
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "4px",
                          color: "white",
                          "&:hover": {
                            background: "rgba(255,255,255,0.28)",
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredCount}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          onRowsPerPageChange(parseInt(e.target.value, 10));
        }}
        sx={{
          color: "#fff",
          position: "relative",
          zIndex: 1,
          mt: 0,
          borderTop: "1px solid rgba(255,255,255,0.12)",
          pb: 0,
          px: 0,
          flexShrink: 0,
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
            {
              color: "#fff",
            },
          "& .MuiInputBase-root": {
            color: "#fff",
          },
          "& .MuiSvgIcon-root": {
            fill: "#fff",
          },
          "& .MuiIconButton-root:hover": {
            background: "rgba(255,255,255,0.1)",
          },
        }}
      />
    </Box>
  );
};

export default PlayersTable;
